/* eslint-disable */
import {
  buzzerMelodies,
  deviceActions,
  devicesConnectionsLedColors,
  PressProfiles
} from '../../util';
import { IColor } from '../ColorSlider/ColorSlider';
import { ISelectOption } from '../Select';
import {
  ConnectionConfig,
  Dock,
  DocksMap,
  IInputDevice,
  SCENE_ACTION
} from './types';

export const shouldPressProfileSelectionForRoomBeVisible = (
  dockNumber: string,
  inputDevice: IInputDevice
) => {
  if (!inputDevice) {
    return false;
  }

  if (inputDevice.startsFrom === 'input') {
    return dockNumber === inputDevice.connectionInputConfig.sourceDockNumber;
  }

  if (inputDevice.startsFrom === 'output') {
    return dockNumber === inputDevice.connectionInputConfig.targetDockNumber;
  }
};

export const shouldPressProfileSelectionForScenesBeVisible = (
  inputDevice: IInputDevice
) =>
  inputDevice &&
  inputDevice.connectionInputConfig &&
  inputDevice.connectionInputConfig.targetAction === SCENE_ACTION;

export const isConnectionValidBasedOnConfiguringDirection = (
  configuringDirection: string,
  connectionsParams: ConnectionConfig,
  connection
): boolean => {
  const {
    sourceDeviceNumber,
    sourceDockNumber,
    targetDeviceNumber,
    targetDockNumber,
    triggerEvent
  } = connectionsParams;
  if (!connection || !connection.isConnectionValid) {
    return false;
  }
  if (configuringDirection === 'output') {
    return connection.isConnectionValid({
      targetDeviceNumber,
      targetDockNumber,
      triggerEvent
    });
  }
  return connection.isConnectionValid({
    targetDeviceNumber,
    targetDockNumber,
    triggerEvent,
    sourceDeviceNumber,
    sourceDockNumber
  });
};

export const getFrameLedSelectedColors = (
  inputDevice: IInputDevice,
  connectionsConfiguration,
  pressProfile: PressProfiles,
  deviceIndex: number,
  dock: Dock
): IColor[] => {
  const { startsFrom, connectionInputConfig } = inputDevice;
  const {
    sourceDockNumber = null,
    sourceDeviceNumber = null,
    targetActionParameters
  } = connectionInputConfig;
  const connectionToCheckParams = {
    sourceDeviceNumber,
    sourceDockNumber,
    targetDeviceNumber: deviceIndex,
    targetDockNumber: dock.number,
    triggerEvent: pressProfile
  };
  const defaultColor = {
    Color: { hue: 0, saturation: 0 },
    Brightness: 0
  } as IColor;

  if (startsFrom === 'output') {
    const configurationsColors = connectionsConfiguration
      .filter(devicesConnection =>
        isConnectionValidBasedOnConfiguringDirection(
          'output',
          connectionToCheckParams,
          devicesConnection
        )
      )
      .map(({ connection }) => {
        if (typeof connection.targetActionParameters.Color !== 'undefined') {
          return {
            Color: connection.targetActionParameters.Color,
            Brightness: connection.targetActionParameters.Brightness
          };
        } else {
          return defaultColor;
        }
      })
      // searching for the same selected colors for any of other frame buttons and filter them to not repeat. Case only when start from output
      .filter(
        (value, index, array) =>
          array.findIndex(({ Color, Brightness }) => {
            if (Color === devicesConnectionsLedColors.default) {
              // selected null as a color
              return value.Color === devicesConnectionsLedColors.default;
            }

            // selected one of standard colors
            return (
              Color.hue === value.Color.hue &&
              Color.saturation === value.Color.saturation &&
              Brightness === value.Brightness
            );
          }) === index
      );

    return configurationsColors.length
      ? configurationsColors.length > 1
        ? // if length is grater than 1 than add on the beginning white color as default value and it will be displayed
          [defaultColor, ...configurationsColors]
        : [...configurationsColors]
      : [
          {
            Color: targetActionParameters.Color,
            Brightness: targetActionParameters.Brightness
          }
        ];
  }

  const connectionActionParameters = connectionsConfiguration.find(
    devicesConnection =>
      isConnectionValidBasedOnConfiguringDirection(
        'input',
        connectionToCheckParams,
        devicesConnection
      )
  )?.connection.targetActionParameters;

  if (connectionActionParameters) {
    const { Color, Brightness } = connectionActionParameters;
    return [{ Color, Brightness }];
  }

  return [defaultColor];
};
export const getBuzzerSelectValue = (
  inputDevice: IInputDevice,
  connectionsConfiguration,
  pressProfile: PressProfiles,
  deviceIndex: number,
  dock: Dock
): { value: string; label: string } => {
  const { startsFrom, connectionInputConfig } = inputDevice;
  const {
    targetActionParameters,
    sourceDockNumber = null,
    sourceDeviceNumber = null
  } = connectionInputConfig;
  const moreSoundsValueText = 'More than 1 sound';
  const connectionToCheckParams = {
    sourceDeviceNumber,
    sourceDockNumber,
    targetDeviceNumber: deviceIndex,
    targetDockNumber: dock.number,
    triggerEvent: pressProfile
  };

  if (startsFrom === 'output') {
    const configurationsSounds = connectionsConfiguration
      .filter(devicesConnection =>
        isConnectionValidBasedOnConfiguringDirection(
          'output',
          connectionToCheckParams,
          devicesConnection
        )
      )
      .map(({ connection }) => connection.targetActionParameters.SoundNumber)
      .filter((value, index, array) => array.indexOf(value) === index);
    const soundValue = configurationsSounds.length
      ? configurationsSounds.length > 1
        ? moreSoundsValueText
        : configurationsSounds.pop()
      : targetActionParameters.SoundNumber;

    return {
      value: soundValue.toString(),
      label: Number.isInteger(soundValue)
        ? buzzerMelodies[soundValue].name
        : moreSoundsValueText
    };
  }

  const configurationSoundValue = connectionsConfiguration.find(
    devicesConnection =>
      isConnectionValidBasedOnConfiguringDirection(
        'input',
        connectionToCheckParams,
        devicesConnection
      )
  ).connection.targetActionParameters.SoundNumber;

  return {
    value: configurationSoundValue.toString(),
    label: buzzerMelodies[configurationSoundValue].name
  };
};

export const onBuzzerMelodySelect = (
  inputDevice: IInputDevice,
  connectionsConfiguration,
  pressProfile: PressProfiles,
  setInputDevice: React.Dispatch<React.SetStateAction<IInputDevice>>,
  deviceIndex: number,
  dock: Dock,
  updateConnections,
  melody: ISelectOption
): void => {
  const { connectionInputConfig } = inputDevice;
  const { targetAction } = connectionInputConfig;

  if (inputDevice.startsFrom === 'output') {
    const deviceAction = deviceActions.find(({ key }) => key === targetAction);
    const targetActionParameters = deviceAction
      ? deviceAction.value(
          // @ts-ignore
          Number.parseInt(melody.value, 10)
        )
      : {};
    const updatedConnections = connectionsConfiguration.map(
      devicesConnection => {
        const { connection } = devicesConnection;
        if (!devicesConnection) return;
        const isConnectionToChange = devicesConnection.isConnectionValid({
          targetDeviceNumber: deviceIndex,
          targetDockNumber: dock.number,
          triggerEvent: pressProfile
        });

        if (isConnectionToChange) {
          devicesConnection.connection = {
            ...connection,
            targetActionParameters
          };
        }

        return devicesConnection;
      }
    );

    setInputDevice({
      ...inputDevice,
      connectionInputConfig: {
        ...connectionInputConfig,
        targetActionParameters
      }
    });
    updateConnections(updatedConnections);
  } else {
    const updatedConnections = connectionsConfiguration.map(
      devicesConnection => {
        const { connection } = devicesConnection;
        if (!devicesConnection) return;
        const connectionToChangeExists = devicesConnection.isConnectionValid({
          sourceDeviceNumber:
            inputDevice.connectionInputConfig.sourceDeviceNumber,
          sourceDockNumber: inputDevice.connectionInputConfig.sourceDockNumber.toString(),
          targetDeviceNumber: deviceIndex,
          targetDockNumber: dock.number,
          triggerEvent: pressProfile
        });
        const deviceAction = deviceActions.find(
          ({ key }) => key === connection.targetAction
        );
        const targetActionParameters = deviceAction
          ? deviceAction.value(
              // @ts-ignore
              Number.parseInt(melody.value, 10)
            )
          : {};

        if (connectionToChangeExists) {
          devicesConnection.connection = {
            ...connection,
            targetActionParameters
          };
        }

        return devicesConnection;
      }
    );
    updateConnections(updatedConnections);
  }
};
export const onFrameLedColorSelect = (
  inputDevice: IInputDevice,
  connectionsConfiguration,
  pressProfile: PressProfiles,
  setInputDevice: React.Dispatch<React.SetStateAction<IInputDevice>>,
  deviceIndex: number,
  dock: Dock,
  updateConnections,
  selectedColor: IColor
): void => {
  const { connectionInputConfig } = inputDevice;
  const { targetAction } = connectionInputConfig;

  if (inputDevice.startsFrom === 'output') {
    const deviceAction = deviceActions.find(({ key }) => key === targetAction);
    const targetActionParameters = deviceAction
      ? // @ts-ignore
        deviceAction.value('TOGGLE', selectedColor)
      : {};
    const updatedConnections = connectionsConfiguration.map(
      devicesConnection => {
        const { connection } = devicesConnection;
        const isConnectionToChange = devicesConnection.isConnectionValid({
          targetDeviceNumber: deviceIndex,
          targetDockNumber: dock.number,
          triggerEvent: pressProfile
        });

        if (isConnectionToChange) {
          devicesConnection.connection = {
            ...connection,
            targetActionParameters
          };
        }

        return devicesConnection;
      }
    );

    setInputDevice({
      ...inputDevice,
      connectionInputConfig: {
        ...connectionInputConfig,
        targetActionParameters
      }
    });

    updateConnections(updatedConnections);
  } else {
    const updatedConnections = connectionsConfiguration.map(
      devicesConnection => {
        const { connection } = devicesConnection;
        const isConnectionToChange = devicesConnection.isConnectionValid({
          sourceDeviceNumber:
            inputDevice.connectionInputConfig.sourceDeviceNumber,
          sourceDockNumber: inputDevice.connectionInputConfig.sourceDockNumber.toString(),
          targetDeviceNumber: deviceIndex,
          targetDockNumber: dock.number,
          triggerEvent: pressProfile
        });
        const deviceAction = deviceActions.find(
          ({ key }) => key === connection.targetAction
        );
        const targetActionParameters = deviceAction
          ? // @ts-ignore
            deviceAction.value('TOGGLE', selectedColor)
          : {};

        if (isConnectionToChange) {
          devicesConnection.connection = {
            ...connection,
            targetActionParameters
          };
        }

        return devicesConnection;
      }
    );
    updateConnections(updatedConnections);
  }
};
